/**
 * The coloured weather field on the map, as a single image pinned to its corners.
 *
 * MapLibre takes an `image` source: four corner coordinates and a picture. We draw
 * the picture ourselves from the model grid (fieldRaster.ts) and hand it over. No
 * tile server, no new dependency, and re-drawing when the hour changes is a couple
 * of milliseconds of canvas work rather than a network round trip.
 *
 * IT GOES UNDERNEATH THE SYMBOLS. That is the whole lesson from the weather apps
 * Edd sent over: colour carries the strength, continuously, and the symbol on top
 * carries only the direction. A wash drawn over the arrows would bury the thing it
 * is meant to be explaining.
 *
 * An image source cannot have its corners changed after it is added, so panning to
 * a new extent means removing the source and adding it again. That sounds heavy and
 * is not: it happens once per fetch, not once per frame.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import { fieldImageUrl, cornersFor, type FieldGrid } from './fieldRaster';
import type { FieldVariable } from './fieldRamps';

const SOURCE_ID = 'weather-field';
const LAYER_ID = 'weather-field-wash';

/**
 * The layer the wash is inserted BELOW.
 *
 * The first of these that exists wins, so the wash lands under the wind symbols
 * and under anything the skipper is actually navigating by. If none of them are
 * there yet, it goes on top, which is the right fallback: a wash nobody can see
 * because it slid under the basemap would look like the feature is broken.
 */
const BELOW = ['weather-arrow', 'weather-barb', 'weather-dot', 'weather-arrow-base'];

function beforeId(map: MaplibreMap): string | undefined {
  return BELOW.find((id) => map.getLayer(id));
}

/** What is currently drawn, so an identical redraw can be skipped. */
let lastKey: string | null = null;

function keyOf(grid: FieldGrid, variable: FieldVariable): string {
  return `${variable.id}:${grid.bounds.join(',')}:${grid.values.join(',')}`;
}

export function removeWeatherFieldLayer(map: MaplibreMap): void {
  lastKey = null;
  if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID);
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
}

/**
 * Draw (or clear) the wash.
 *
 * Pass a null grid for "nothing to show": no field loaded, the variable has no
 * ramp, or the overlay is off. It clears rather than leaving the last hour's
 * colours on screen, because a wash that quietly belongs to a different time is
 * worse than no wash.
 */
export function ensureWeatherFieldLayer(
  map: MaplibreMap,
  grid: FieldGrid | null,
  variable: FieldVariable | null,
  opacity = 1,
): void {
  if (!grid || !variable) {
    removeWeatherFieldLayer(map);
    return;
  }
  const key = keyOf(grid, variable);
  if (key === lastKey && map.getLayer(LAYER_ID)) return;

  const url = fieldImageUrl(grid, variable.stops, 256, 256, {
    alpha: variable.alpha,
    hideBelow: variable.hideBelow,
  });
  // No canvas (tests, or a WebView that has just lost its renderer): draw nothing
  // rather than throwing halfway through building the style.
  if (!url) return;

  removeWeatherFieldLayer(map);
  map.addSource(SOURCE_ID, { type: 'image', url, coordinates: cornersFor(grid.bounds) });
  map.addLayer(
    {
      id: LAYER_ID,
      type: 'raster',
      source: SOURCE_ID,
      paint: { 'raster-opacity': opacity, 'raster-fade-duration': 0 },
    },
    beforeId(map),
  );
  lastKey = key;
}
